import { Box, HStack, Text, IconButton, Container } from "@chakra-ui/react";
import { MdArrowBack as BackIcon, MdRefresh as RefreshIcon, MdHelpOutline as HelpIcon } from "react-icons/md";
import { useSearch } from "wouter";
import { useAIChatController } from "@/hooks/useAIChatController";
import ChatConversation from "@/components/ChatConversation";

export default function AIChefChatPage() {
  const search = useSearch();
  const initialHelpMode = new URLSearchParams(search).get('from') === 'help';

  const {
    recipeId,
    messages,
    isLoading,
    currentRecipe,
    currentCollection,
    pendingAction,
    confirmAction,
    cancelAction,
    isConfirming,
    helpMode,
    resetChat,
    handleHelp,
    onSend,
  } = useAIChatController({ initialHelpMode });

  // Once conversation starts the welcome card is gone — reveal identity in nav
  const inConversation = messages.length > 0 || isLoading;

  return (
    <Box h="100dvh" display="flex" flexDirection="column" bg="bg.canvas" position="relative">
      {/* Safe-area spacer */}
      <Box h="env(safe-area-inset-top, 0px)" bg="bg.canvas" flexShrink={0} />

      {/* Floating controls — always present, positioned over content */}
      <HStack
        position="fixed"
        top="calc(env(safe-area-inset-top, 0px) + 10px)"
        left={0}
        right={0}
        zIndex={20}
        px={3}
        justify="space-between"
        align="center"
        pointerEvents="none"
      >
        {/* Back pill */}
        <IconButton
          aria-label="Go back"
          size="sm"
          variant="outline"
          color="text.secondary"
          bg="rgba(245,241,234,0.88)"
          borderColor="border.muted"
          borderRadius="full"
          w="36px"
          h="36px"
          minW="36px"
          flexShrink={0}
          pointerEvents="auto"
          _hover={{ bg: 'bg.surface', borderColor: 'border.default' }}
          onClick={() => window.history.back()}
          style={{ backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' } as React.CSSProperties}
        >
          <BackIcon />
        </IconButton>

        {/* Help + Reset */}
        <HStack gap={0.5} pointerEvents="auto">
          <IconButton
            aria-label="Help"
            size="sm"
            variant="ghost"
            color={helpMode ? 'accent.secondary' : 'text.muted'}
            borderRadius="full"
            onClick={handleHelp}
            _hover={{ bg: 'rgba(0,0,0,0.06)' }}
          >
            <HelpIcon />
          </IconButton>
          <IconButton
            aria-label="Reset chat"
            size="sm"
            variant="ghost"
            color="text.muted"
            borderRadius="full"
            onClick={resetChat}
            disabled={messages.length === 0}
            _hover={{ bg: 'rgba(0,0,0,0.06)' }}
          >
            <RefreshIcon />
          </IconButton>
        </HStack>
      </HStack>

      {/* Thin identity strip — same bg as page, just a hairline + centred label in chat */}
      <HStack
        position="fixed"
        top="env(safe-area-inset-top, 0px)"
        left={0}
        right={0}
        zIndex={19}
        h="56px"
        px={3}
        gap={2}
        align="center"
        bg="bg.canvas"
        borderBottom="1px solid"
        borderBottomColor={inConversation ? 'border.muted' : 'transparent'}
        transition="border-color 0.3s"
        pointerEvents="none"
      >
        <Box w="44px" flexShrink={0} />
        <HStack gap={2} flex={1} minW={0} justify="center">
          <Box w="32px" h="32px" flexShrink={0}>
            <img src="/favicon.png" alt="" width={32} height={32} style={{ display: 'block', objectFit: 'contain' }} />
          </Box>
          <Text fontFamily="heading" fontWeight="800" fontSize="md" color="text.primary" lineHeight="1">
            Chef Assistant
          </Text>
          {(currentRecipe?.title ?? currentCollection?.title) && (
            <Text fontSize="10px" color="accent.secondary" fontWeight="600" letterSpacing="0.03em"
              overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
              · {currentRecipe?.title ?? currentCollection?.title}
            </Text>
          )}
        </HStack>
        <Box w="44px" flexShrink={0} />
      </HStack>

      <Container
        maxW="container.lg"
        flex="1"
        overflow="hidden"
        display="flex"
        flexDirection="column"
        px={0}
        pt="56px"
      >
        <ChatConversation
          messages={messages}
          isLoading={isLoading}
          onSend={onSend}
          recipeId={recipeId}
          currentRecipe={currentRecipe}
          currentCollection={currentCollection}
          helpMode={helpMode}
          pendingAction={pendingAction}
          onConfirmAction={confirmAction}
          onCancelAction={cancelAction}
          isConfirmingAction={isConfirming}
        />
      </Container>
    </Box>
  );
}
